import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, Sun, Moon, Loader2, Mail, Phone, BookOpen, Users, Award, Sparkles, KeyRound, ArrowLeft, ChartNoAxesCombined } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import axios from 'axios';

// Import local logo assets
import TRSLogo from '../assets/logos/TRS_logo.png';
import ParikramaLogo from '../assets/logos/Parikrama_logo.png';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// ScatterPie white logo for login page (left panel on dark background)
const SCATTERPIE_WHITE_LOGO = 'https://customer-assets.emergentagent.com/job_skillsforge-1/artifacts/ocgjk4og_scatterpie-blue-logo.png';

// Detect if user is on a mobile device
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Check for common mobile device patterns
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
  // Also check screen width as a fallback
  const isSmallScreen = window.innerWidth <= 768;
  return mobileRegex.test(userAgent.toLowerCase()) || isSmallScreen;
};

export const Login = () => {
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('site') || searchParams.get('site_id');
  const loginMode = searchParams.get('mode'); // 'oem' for sales admin login
  
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Default to 'mobile' login for all users
  const [loginType, setLoginType] = useState('mobile');
  const [tenantInfo, setTenantInfo] = useState(null); // Store tenant info for site_id
  
  // OTP Login State
  const [otpMode, setOtpMode] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [checkingOtp, setCheckingOtp] = useState(false);
  
  const [branding, setBranding] = useState({
    app_name: 'Parikrama',
    slogan: 'Rising of Knowledge',
    primary_color: '#2563EB',
    logo_url: null,
  });
  const { loginWithCredentials, setAuthState } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { refreshBranding } = useBranding();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // For OEM mode, set Sales Admin branding
    if (loginMode === 'oem') {
      setBranding({
        app_name: 'Parikrama',
        slogan: 'Sales Administration Portal',
        primary_color: '#7c3aed', // Purple for sales admin
        logo_url: null,
      });
      return;
    }
    
    // Fetch branding settings - tenant-specific if site_id provided
    const fetchBranding = async () => {
      try {
        let url = `${API_URL}/settings/branding/public`;
        
        // If site_id is provided, fetch tenant-specific branding
        if (siteId) {
          // First validate and get tenant info
          try {
            const tenantRes = await axios.get(`${API_URL}/oem/tenant-by-site/${siteId}`);
            if (tenantRes.data) {
              setTenantInfo(tenantRes.data);
              // Use tenant_id to fetch branding
              url = `${API_URL}/settings/branding/public?tenant_id=${tenantRes.data.tenant_id}`;
              
              // If tenant has custom branding, use it
              if (tenantRes.data.branding) {
                setBranding(prev => ({
                  ...prev,
                  app_name: tenantRes.data.branding.app_name || tenantRes.data.company_name || prev.app_name,
                  slogan: tenantRes.data.branding.slogan || prev.slogan,
                  logo_url: tenantRes.data.branding.logo_url || prev.logo_url,
                  primary_color: tenantRes.data.branding.primary_color || prev.primary_color,
                }));
                if (tenantRes.data.branding.primary_color) {
                  document.documentElement.style.setProperty('--brand-primary', tenantRes.data.branding.primary_color);
                }
                return; // Don't fetch generic branding
              }
            }
          } catch (tenantErr) {
            console.log('Tenant not found for site_id:', siteId);
          }
        }
        
        const response = await axios.get(url);
        if (response.data) {
          setBranding(prev => ({ 
            ...prev, 
            primary_color: response.data.primary_color || '#2563EB',
            logo_url: response.data.logo_url || prev.logo_url,
            app_name: response.data.app_name || prev.app_name,
          }));
          if (response.data.primary_color) {
            document.documentElement.style.setProperty('--brand-primary', response.data.primary_color);
          }
        }
      } catch (error) {
        console.log('Using default branding');
      }
    };
    fetchBranding();
  }, [siteId, loginMode]);

  // Clear identifier when switching login type
  useEffect(() => {
    setIdentifier('');
    setOtpMode(false);
    setOtpSent(false);
    setOtpValue('');
    setOtpEnabled(false);
  }, [loginType]);

  // Check if OTP is enabled for mobile number
  const checkOtpEnabled = async (mobile) => {
    if (mobile.length !== 10) {
      setOtpEnabled(false);
      return;
    }
    setCheckingOtp(true);
    try {
      const response = await axios.get(`${API_URL}/auth/otp/check-enabled`, {
        params: { mobile_number: mobile, site_id: siteId }
      });
      setOtpEnabled(response.data.otp_enabled);
    } catch (error) {
      setOtpEnabled(false);
    } finally {
      setCheckingOtp(false);
    }
  };

  // Send OTP handler
  const handleSendOtp = async () => {
    if (!identifier || identifier.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    setOtpSending(true);
    try {
      await axios.post(`${API_URL}/auth/otp/send`, {
        mobile_number: identifier,
        site_id: siteId
      });
      toast.success('OTP sent successfully! Check your mobile.');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  // OTP Login handler
  const handleOtpLogin = async (e) => {
    e.preventDefault();
    if (!otpValue || otpValue.length !== 4) {
      toast.error('Please enter the 4-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/otp/verify`, {
        mobile_number: identifier,
        otp: otpValue,
        site_id: siteId
      });
      
      const { access_token, user } = response.data;
      
      // Update AuthContext state (this triggers proper re-render)
      setAuthState(access_token, user);
      
      // Load user's language preference
      if (user?.preferred_language) {
        i18n.changeLanguage(user.preferred_language);
        localStorage.setItem('preferredLanguage', user.preferred_language);
      }
      
      // Refresh branding after login to get user's org branding
      refreshBranding();
      
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate input based on login type (skip for OEM mode which uses email only)
    if (loginMode !== 'oem') {
      if (loginType === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(identifier)) {
          toast.error(t('auth.invalidEmail') || 'Please enter a valid email address');
          return;
        }
      } else {
        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(identifier)) {
          toast.error(t('auth.invalidMobile') || 'Please enter a valid 10-digit mobile number');
          return;
        }
      }
    } else {
      // OEM mode - validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(identifier)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }
    
    setLoading(true);
    try {
      if (loginMode === 'oem') {
        // OEM/Sales Admin login
        const response = await axios.post(`${API_URL}/oem/auth/login`, { 
          email: identifier, 
          password 
        });
        const { access_token, user } = response.data;
        localStorage.setItem('oem_token', access_token);
        localStorage.setItem('oem_user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        toast.success('Login successful!');
        navigate('/sales-admin');
      } else {
        // Regular user login
        // Pass site_id for cross-tenant login prevention
        const result = await loginWithCredentials(identifier, password, loginType, siteId);
        
        // Load user's language preference (result contains { user, forcePasswordReset })
        if (result?.user?.preferred_language) {
          i18n.changeLanguage(result.user.preferred_language);
          localStorage.setItem('preferredLanguage', result.user.preferred_language);
        }
        
        // Refresh branding after login to get user's org branding
        refreshBranding();
        
        toast.success(t('auth.loginSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  // Feature highlights for the left panel
  const features = [
    {
      icon: BookOpen,
      title: 'Structured Learning Pathway',
      description: 'Step-by-step modules built to develop expertise.'
    },
    {
      icon: Users,
      title: 'Interactive & Collaborative Ecosystem',
      description: 'Collaborate with peers and mentors through real-world learning.'
    },
    {
      icon: Award,
      title: 'Milestones & Certifications',
      description: 'Earn credentials that validate your growth.'
    },
    {
      icon: ChartNoAxesCombined,
      title: 'Performance-Driven Insights',
      description: 'Measure progress with actionable learning analytics.'
    },
    // {
    //   icon: Sparkles,
    //   title: 'AI-Enabled Personalization',
    //   description: 'Experience intelligent course recommendations tailored to your role, performance gaps, and career aspirations.'
    // },
  ];

  return (
    <div className="h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* Left Panel - Branding & Features */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e1b4b 100%)`
        }}
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          {/* Animated gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Subtle pattern overlay */}
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between px-8 xl:px-16 py-8 h-full">
          {/* Top Section - ScatterPie Logo */}
          <div className="flex items-center justify-start mb-4">
            {/* ScatterPie Logo */}
            <img 
              src={SCATTERPIE_WHITE_LOGO} 
              alt="ScatterPie Analytics" 
              className="h-10 w-auto brightness-0 invert drop-shadow-2xl"
              data-testid="scatterpie-logo"
            />
          </div>
          
          {/* Middle Section - Branding & Features */}
          <div className="flex-1 flex flex-col justify-center">
            {/* Brand Text with Parikrama Logo */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                {/* Parikrama Logo beside title */}
                <img 
                  src={ParikramaLogo} 
                  alt="Parikrama" 
                  className="h-12 w-12 object-contain rounded-full"
                  data-testid="parikrama-logo"
                />
                <h1 className="text-3xl xl:text-4xl font-bold text-white tracking-tight">
                  Parikrama
                </h1>
              </div>
              <p className="text-lg text-orange-300/90 font-medium">
                परिक्रमा — The Journey of Continuous Growth
              </p>
              <p className="text-gray-300/80 mt-2 text-base max-w-md leading-relaxed">
                Embark on a purposeful learning journey where every step builds mastery.
              </p>
            </div>

            {/* Feature Highlights */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 group"
                >
                <div 
                  className="p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 
                    group-hover:bg-white/20 group-hover:border-orange-400/30 transition-all duration-300"
                >
                  <feature.icon className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-snug">{feature.description}</p>
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* Bottom Quote */}
          <div className="pt-4 border-t border-white/10">
            <blockquote className="text-gray-300/90 italic text-base">
              "ज्ञानं परं बलम्" 
            </blockquote>
            <p className="text-gray-500 text-xs mt-1">
              Knowledge is the supreme strength.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col bg-gradient-to-br from-background via-background to-muted/50 h-screen overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between p-4 md:p-6">
          {/* Mobile Logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <img 
              src={SCATTERPIE_WHITE_LOGO} 
              alt="ScatterPie Analytics" 
              className="h-7 w-auto"
              data-testid="scatterpie-logo-mobile"
            />
            {/* Parikrama Logo for mobile */}
            <img 
              src={ParikramaLogo} 
              alt="Parikrama" 
              className="h-8 w-8 object-contain rounded-full"
              data-testid="parikrama-logo-mobile"
            />
            <div>
              <h1 className="text-base font-bold text-foreground">Parikrama</h1>
              <p className="text-xs text-muted-foreground">The Journey of Growth</p>
            </div>
          </div>
          <div className="lg:flex-1" />
          <div className="flex items-center gap-2">
            <LanguageSelector showLabel={false} />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-6">
          <div className="w-full max-w-md">
            <Card className="border-0 shadow-2xl bg-card/95 backdrop-blur-sm">
              <CardHeader className="text-center pb-1 pt-4">
                {loginMode === 'oem' ? (
                  <>
                    <CardTitle className="text-2xl font-bold">Sales Admin Portal</CardTitle>
                    <CardDescription className="text-sm">
                      Enter your credentials to access the admin panel
                    </CardDescription>
                  </>
                ) : (
                  <>
                    {/* TRS (Raymond) Logo above Welcome Back - bigger, no background */}
                    <div className="flex justify-center mb-3">
                      <img 
                        src={TRSLogo} 
                        alt="The Raymond Shop" 
                        className="h-20 w-auto object-contain"
                        data-testid="trs-logo"
                      />
                    </div>
                    <CardTitle className="text-2xl font-bold">{t('auth.welcomeBack')}</CardTitle>
                    <CardDescription className="text-sm">
                      {t('auth.enterCredentials')}
                    </CardDescription>
                  </>
                )}
              </CardHeader>
              <CardContent className="pt-2 pb-4">
                {/* OEM Mode: Email only login */}
                {loginMode === 'oem' ? (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="email" className="text-sm">{t('auth.email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="admin@company.com"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        required
                        data-testid="login-email"
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="password" className="text-sm">{t('auth.password')}</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          data-testid="login-password"
                          className="h-10 pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="toggle-password"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-10 text-sm font-semibold"
                      style={{ backgroundColor: branding.primary_color }}
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('auth.signingIn')}
                        </>
                      ) : (
                        t('auth.signIn')
                      )}
                    </Button>
                  </form>
                ) : (
                  /* Regular Mode: Email/Mobile tabs */
                  <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-3">
                    <TabsTrigger value="email" data-testid="login-tab-email" className="flex items-center gap-2 text-sm">
                      <Mail className="w-3 h-3" />
                      {t('auth.email')}
                    </TabsTrigger>
                    <TabsTrigger value="mobile" data-testid="login-tab-mobile" className="flex items-center gap-2 text-sm">
                      <Phone className="w-3 h-3" />
                      {t('auth.mobile') || 'Mobile'}
                    </TabsTrigger>
                  </TabsList>
                  
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <TabsContent value="email" className="mt-0 space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="email" className="text-sm">{t('auth.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@company.com"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          required
                          data-testid="login-email"
                          className="h-10"
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="mobile" className="mt-0 space-y-3">
                      <div className="space-y-1">
                        <Input
                          id="mobile"
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={identifier}
                          onChange={(e) => {
                            const mobile = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setIdentifier(mobile);
                            // Reset OTP state when mobile changes
                            if (otpSent) {
                              setOtpSent(false);
                              setOtpValue('');
                            }
                          }}
                          required
                          disabled={otpSent}
                          data-testid="login-mobile"
                          className="h-10"
                          maxLength={10}
                        />
                      </div>
                      
                      {/* OTP Flow - Always OTP based for mobile login */}
                      {!otpSent ? (
                        <Button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={otpSending || identifier.length !== 10}
                          className="w-full h-10 text-sm font-semibold rounded-lg"
                          data-testid="send-otp-btn"
                          style={{ 
                            background: `linear-gradient(135deg, #f97316 0%, ${branding.primary_color} 100%)`,
                            boxShadow: '0 4px 14px 0 rgba(249, 115, 22, 0.39)'
                          }}
                        >
                          {otpSending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending OTP...
                            </>
                          ) : (
                            <>
                              <Phone className="w-4 h-4 mr-2" />
                              Send OTP
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="space-y-3">
                          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                            <p className="text-sm text-green-800 dark:text-green-200">
                              OTP sent to {identifier.slice(0, 3)}****{identifier.slice(-3)}
                            </p>
                          </div>
                          
                          <div className="space-y-1">
                            <Input
                              id="otp"
                              type="text"
                              placeholder="Enter OTP"
                              value={otpValue}
                              onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="h-12 text-center text-2xl tracking-widest font-mono"
                              maxLength={6}
                              data-testid="otp-input"
                              autoFocus
                            />
                          </div>
                          
                          <Button
                            type="button"
                            onClick={handleOtpLogin}
                            className="w-full h-10 text-sm font-semibold rounded-lg"
                            disabled={loading || otpValue.length < 4}
                            data-testid="verify-otp-btn"
                            style={{ 
                              background: `linear-gradient(135deg, #f97316 0%, ${branding.primary_color} 100%)`,
                              boxShadow: '0 4px 14px 0 rgba(249, 115, 22, 0.39)'
                            }}
                          >
                            {loading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Verifying...
                              </>
                            ) : (
                              <>
                                <KeyRound className="w-4 h-4 mr-2" />
                                Verify & Login
                              </>
                            )}
                          </Button>
                          
                          <div className="flex items-center justify-between">
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              onClick={handleSendOtp}
                              disabled={otpSending}
                              className="text-xs p-0 h-auto"
                            >
                              {otpSending ? 'Resending...' : 'Resend OTP'}
                            </Button>
                            
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              onClick={() => {
                                setOtpSent(false);
                                setOtpValue('');
                              }}
                              className="text-xs p-0 h-auto text-muted-foreground"
                            >
                              Change Number
                            </Button>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Password field - only show for email login */}
                    {loginType === 'email' && (
                      <div className="space-y-1">
                        <Label htmlFor="password" className="text-sm">{t('auth.password')}</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="login-password"
                            className="h-10 pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                            onClick={() => setShowPassword(!showPassword)}
                            data-testid="toggle-password"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Submit button - only show for email login */}
                    {loginType === 'email' && (
                      <Button
                        type="submit"
                        className="w-full h-10 text-sm font-semibold rounded-lg"
                        disabled={loading}
                        data-testid="login-submit"
                        style={{ 
                          background: `linear-gradient(135deg, #f97316 0%, ${branding.primary_color} 100%)`,
                          boxShadow: '0 4px 14px 0 rgba(249, 115, 22, 0.39)'
                        }}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('common.loading')}
                          </>
                        ) : (
                          t('auth.signIn')
                        )}
                      </Button>
                    )}
                  </form>
                </Tabs>
                )}
              </CardContent>
            </Card>
            
            {/* Trust Indicators */}
            <div className="mt-3 text-center">
              <p className="text-xs text-muted-foreground">
                Trusted by organizations worldwide
              </p>
              <div className="flex items-center justify-center gap-3 mt-1 opacity-60">
                <div className="text-xs text-muted-foreground font-medium">ISO 27001</div>
                <div className="w-1 h-1 rounded-full bg-muted-foreground" />
                <div className="text-xs text-muted-foreground font-medium">GDPR</div>
                <div className="w-1 h-1 rounded-full bg-muted-foreground" />
                <div className="text-xs text-muted-foreground font-medium">Enterprise Ready</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Compact */}
        <footer className="py-1 text-center text-xs text-muted-foreground flex-shrink-0">
          © 2025 Gyanodaya. Powered by <a href="https://www.scatterpie.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">ScatterPie Analytics</a>
        </footer>
      </div>
    </div>
  );
};

export default Login;
